<template>
  <Card :title="expired ? '' : '開立發票'">
    <!-- 開票逾時未動作:發票將自動捐贈(由 server 自動開立),再倒數 10 秒自動關閉 -->
    <div v-if="expired" class="donate-notice">
      <div class="donate-title">發票將自動捐贈</div>
      <p class="donate-desc">您未於時間內開立發票,本次消費將自動捐贈發票,感謝您的愛心。</p>
      <div class="donate-close">{{ closeIn }} 秒後自動關閉</div>
    </div>

    <template v-else>
      <OrderSummary :amount="session.amount" />

      <div class="countdown" :class="{ warn: remaining <= 60 }">
        <span class="countdown-label">剩餘時間</span>
        <span class="countdown-value">{{ mmss }}</span>
        <span class="countdown-hint">逾時將自動捐贈</span>
      </div>

      <div v-if="mode === 'carrier'" class="form">
        <label class="field">
          <span class="field-label">手機條碼</span>
          <input
            v-model.trim="carrierId"
            class="control"
            placeholder="/ABC.123"
            autocomplete="off"
            maxlength="8"
            autofocus
          />
        </label>
      </div>

      <p v-if="error" class="err">{{ error }}</p>
      <p v-if="validationMsg" class="err">{{ validationMsg }}</p>
    </template>

    <template #footer>
      <button
        v-if="expired"
        class="btn btn-primary full"
        @click="$emit('close')"
      >
        關閉
      </button>

      <div v-else class="actions">
        <template v-if="mode === 'default'">
          <button class="btn btn-primary full" :disabled="submitting" @click="onDonate">
            <Spinner v-if="submitting" />
            <span v-else>愛心捐贈</span>
          </button>
          <button class="btn btn-ghost full" :disabled="submitting" @click="enterCarrier">
            開立發票
          </button>
        </template>

        <template v-else>
          <button class="btn btn-primary full" :disabled="submitting" @click="onConfirm">
            <Spinner v-if="submitting" />
            <span v-else>確認</span>
          </button>
          <button class="btn btn-ghost full" :disabled="submitting" @click="onDonate">
            愛心捐贈
          </button>
        </template>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Card from './Card.vue'
import OrderSummary from './OrderSummary.vue'
import Spinner from './Spinner.vue'
import { CARRIER_DONATE, CARRIER_MOBILE, type QrInvoiceBo, type QrSessionVo } from '@/types/qr'

const INVOICE_TIMEOUT_SECONDS = 600
/** 逾時後顯示「發票將自動捐贈」再倒數幾秒關閉畫面 */
const CLOSE_SECONDS = 10

const props = defineProps<{
  session: QrSessionVo
  submitting?: boolean
  error?: string
  /** 進入 PAID 的 epoch ms,用來算 10 分鐘倒數 */
  paidAt: number
}>()

const emit = defineEmits<{
  (e: 'submit', body: QrInvoiceBo): void
  (e: 'close'): void
}>()

const mode = ref<'default' | 'carrier'>('default')
const carrierId = ref('')
const validationMsg = ref('')

const now = ref(Date.now())
let tickTimer: number | null = null

const remaining = computed(() => {
  const elapsed = Math.floor((now.value - props.paidAt) / 1000)
  return Math.max(0, INVOICE_TIMEOUT_SECONDS - elapsed)
})
const mmss = computed(() => {
  const m = Math.floor(remaining.value / 60)
  const s = remaining.value % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
})

/** 開票倒數歸零 → 進入「發票將自動捐贈」狀態,再倒數 CLOSE_SECONDS 關閉 */
const expired = ref(false)
const expiredAt = ref(0)
const closeIn = computed(() => {
  if (!expired.value) return CLOSE_SECONDS
  return Math.max(0, CLOSE_SECONDS - Math.floor((now.value - expiredAt.value) / 1000))
})

function startTimer() {
  stopTimer()
  now.value = Date.now()
  tickTimer = window.setInterval(() => {
    now.value = Date.now()
  }, 1000)
}
function stopTimer() {
  if (tickTimer != null) {
    window.clearInterval(tickTimer)
    tickTimer = null
  }
}

/* 開票時間到 → 標記逾時(不主動送捐贈:訂單建立時已 invoiceStatus=WAIT,由 server autoInvoice 自動開捐贈)。
   immediate:重整後 session 已逾時(remaining 一開始就 0)也要進入捐贈畫面。 */
watch(
  remaining,
  (v) => {
    if (v === 0 && !expired.value) {
      expired.value = true
      expiredAt.value = now.value
    }
  },
  { immediate: true },
)
/* 自動捐贈提示再倒數結束 → 關閉畫面 */
watch(closeIn, (v) => {
  if (expired.value && v === 0) emit('close')
})

onMounted(startTimer)
onBeforeUnmount(stopTimer)
watch(
  () => props.paidAt,
  () => {
    expired.value = false
    startTimer()
  },
)

function validate(): string {
  if (!/^\/[A-Z0-9.\-+]{7}$/.test(carrierId.value)) {
    return '手機條碼格式錯誤(/ 加 7 碼)'
  }
  return ''
}

function enterCarrier() {
  validationMsg.value = ''
  mode.value = 'carrier'
}

function onConfirm() {
  const msg = validate()
  validationMsg.value = msg
  if (msg) return
  emit('submit', {
    carrierType: CARRIER_MOBILE,
    carrierId: carrierId.value,
  })
}

function onDonate() {
  validationMsg.value = ''
  emit('submit', { carrierType: CARRIER_DONATE })
}
</script>

<style scoped>
.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field-label {
  font-size: 13px;
  color: #6b7280;
}
.control {
  appearance: none;
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 16px;
  background: #fff;
  color: #111827;
  box-sizing: border-box;
}
.control:focus {
  outline: none;
  border-color: #ff6b35;
  box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.2);
}
.err {
  color: #dc2626;
  font-size: 13px;
  margin: 8px 0 0;
}
.actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}
.full {
  width: 100%;
}
.countdown {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 10px;
  align-items: baseline;
  margin: 12px 0 4px;
  padding: 10px 4px;
}
.countdown-label {
  font-size: 13px;
  color: #9a3412;
}
.countdown-value {
  font-size: 22px;
  font-weight: 700;
  color: #ea580c;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.countdown-hint {
  grid-column: 1 / -1;
  font-size: 12px;
  color: #9a3412;
}
.countdown.warn .countdown-value {
  color: #b91c1c;
}
.countdown.warn .countdown-label,
.countdown.warn .countdown-hint {
  color: #b91c1c;
}
.donate-notice {
  text-align: center;
  padding: 20px 8px 4px;
}
.donate-title {
  font-size: 20px;
  font-weight: 700;
  color: #ea580c;
  margin-bottom: 10px;
}
.donate-desc {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 18px;
}
.donate-close {
  font-size: 15px;
  font-weight: 600;
  color: #9a3412;
  font-variant-numeric: tabular-nums;
}
</style>
